import Hashids from 'hashids'

import { Form, GetAppShowResponse, GetBadgeGroupsShowResponse, GetCollectionItemsIndexResponse, GetCollectionsShowResponse, GetMediaItemsShowResponse, GetQuizzesShowResponse, GetScreensShowResponse, Manifest } from 'mytours-read-api-typescript'

export default class STQRYBunnyReadApi {
    SIMILAR_LANGUAGES = {
        en: ["en", "en-GB", "en-AU", "en-CA", "en-IN", "en-SG"],
        es: ["es", "es-419", "es-MX", "es-US"],
        pt: ["pt", "pt-BR", "pt-PT"],
        fr: ["fr", "fr-CA", "fr-FR"]
    }

    host: string
    parentProjectId: number
    tokenType: string
    tokenUUID: string
    hashids: Hashids

    constructor(host: string, parentProjectId: number, tokenType: string, tokenUUID: string, publishingCryptKey: string) {
        this.host = host
        this.parentProjectId = parentProjectId
        this.tokenType = tokenType
        this.tokenUUID = tokenUUID
        // `hFCSfUiIHtuTsc` seems to be the default Ruby `seps`
        this.hashids = new Hashids(publishingCryptKey, 10, "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789", "hFCSfUiIHtuTsc")
    }

    async fetchApp(accountId: number): Promise<GetAppShowResponse> {
        return this.fetchResource(accountId, `/app.json?t=${Date.now()}`)
    }

    async fetchManifest(accountId: number): Promise<Manifest> {
        return this.fetchResource(accountId, `/manifest.json?t=${Date.now()}`)
    }

    async fetchScreen(accountId: number, screenId: number, screenVersion: number, language: string): Promise<GetScreensShowResponse> {
        const languagePathBuilder = this.createLanguagePathBuilder(`/screens/${this.hashids.encode(screenId)}/metadata{language}.json?t=${screenVersion}`)
        const fallbackPaths = this.getFallbackPaths(languagePathBuilder, language)
        return this.fetchResource(accountId, languagePathBuilder(language), fallbackPaths)
    }

    async fetchCollection(accountId: number, collectionId: number, collectionVersion: number, language: string): Promise<GetCollectionsShowResponse> {
        const languagePathBuilder = this.createLanguagePathBuilder(`/collections/${this.hashids.encode(collectionId)}/metadata{language}.json?t=${collectionVersion}`)
        const fallbackPaths = this.getFallbackPaths(languagePathBuilder, language)
        return this.fetchResource(accountId, languagePathBuilder(language), fallbackPaths)
    }

    async fetchCollectionItems(accountId: number, collectionId: number, collectionVersion: number, language: string): Promise<GetCollectionItemsIndexResponse> {
        const languagePathBuilder = this.createLanguagePathBuilder(`/collections/${this.hashids.encode(collectionId)}/items/metadata{language}.json?t=${collectionVersion}`)
        const fallbackPaths = this.getFallbackPaths(languagePathBuilder, language)
        return this.fetchResource(accountId, languagePathBuilder(language), fallbackPaths)
    }

    async fetchMapRoute(accountId: number, mapRouteId: number, mapRouteVersion: number, language: string): Promise<Form> {
        const languagePathBuilder = this.createLanguagePathBuilder(`/map_routes/${this.hashids.encode(mapRouteId)}/metadata{language}.json?t=${mapRouteVersion}`)
        const fallbackPaths = this.getFallbackPaths(languagePathBuilder, language)
        return this.fetchResource(accountId, languagePathBuilder(language), fallbackPaths)
    }

    async fetchMediaItem(accountId: number, mediaItemId: number, mediaItemVersion: number, language: string): Promise<GetMediaItemsShowResponse> {
        const languagePathBuilder = this.createLanguagePathBuilder(`/media_items/${this.hashids.encode(mediaItemId)}/metadata{language}.json?t=${mediaItemVersion}`)
        const fallbackPaths = this.getFallbackPaths(languagePathBuilder, language)
        return this.fetchResource(accountId, languagePathBuilder(language), fallbackPaths)
    }

    async fetchQuiz(accountId: number, quizId: number, quizVersion: number, language: string): Promise<GetQuizzesShowResponse> {
        const languagePathBuilder = this.createLanguagePathBuilder(`/quizzes/${this.hashids.encode(quizId)}/metadata{language}.json?t=${quizVersion}`)
        const fallbackPaths = this.getFallbackPaths(languagePathBuilder, language)
        return this.fetchResource(accountId, languagePathBuilder(language), fallbackPaths)
    }

    async fetchBadgeGroup(accountId: number, badgeGroupId: number, badgeGroupVersion: number, language: string): Promise<GetBadgeGroupsShowResponse> {
        const languagePathBuilder = this.createLanguagePathBuilder(`/badge_groups/${this.hashids.encode(badgeGroupId)}/metadata{language}.json?t=${badgeGroupVersion}`)
        const fallbackPaths = this.getFallbackPaths(languagePathBuilder, language)
        return this.fetchResource(accountId, languagePathBuilder(language), fallbackPaths)
    }

    async fetchForm(accountId: number, formId: number, formVersion: number, language: string): Promise<Form> {
        const languagePathBuilder = this.createLanguagePathBuilder(`/crm/forms/${this.hashids.encode(formId)}/metadata{language}.json?t=${formVersion}`)
        const fallbackPaths = this.getFallbackPaths(languagePathBuilder, language)
        return this.fetchResource(accountId, languagePathBuilder(language), fallbackPaths)
    }

    private createLanguagePathBuilder(templatedPath: string) {
        return (language?: string) => {
            return templatedPath.replace('{language}', language ? `-${language}` : '')
        }
    }

    private getFallbackPaths(languagePathBuilder: (language?: string) => string, language: string) {
        const baseLanguage = language.split('-')[0]
        const similarLanguages = (baseLanguage in this.SIMILAR_LANGUAGES) ? this.SIMILAR_LANGUAGES[baseLanguage as keyof typeof this.SIMILAR_LANGUAGES] : []
        const fallbackPaths = similarLanguages.filter(lang => lang !== language).map(lang => languagePathBuilder(lang))
        fallbackPaths.push(languagePathBuilder())
        return fallbackPaths
    }

    private async fetchResource(accountId: number, path: string, fallbackPaths: string[] = []) {
        const url = `${this.host}/${this.projectAndAccountPrefix(accountId)}${path}`
        const resourceResponse = await this.fetch(url)

        if (resourceResponse.status === 200) {
            return resourceResponse.json()
        }

        if (fallbackPaths.length === 0) {
            throw resourceResponse;
        }

        let fallbackResponse: Response | undefined;
        for (const fallbackPath of fallbackPaths) {
            // Fallback to the primary language if the requested language is not available
            const fallbackUrl = `${this.host}/${this.projectAndAccountPrefix(accountId)}${fallbackPath}`
            fallbackResponse = await this.fetch(fallbackUrl)
            if (fallbackResponse.status === 200) {
                return fallbackResponse.json()
            }
        }

        throw fallbackResponse;
    }

    private projectAndAccountPrefix(accountId: number) {
        return `${this.hashids.encode(this.parentProjectId)}/${this.hashids.encode(accountId)}`
    }

    private fetch(input: RequestInfo | URL, init: RequestInit = {}) {
        return fetch(input, {
            ...init,
            headers: {
                ...(init.headers || {}),
                Authorization: 'Basic ' + btoa(this.parentProjectId + ':' + this.tokenUUID),
                'stqry-project-type': this.tokenType
            }
        })
    }
}
